// Admin client for migration-service (Tools → Data Migration).
//
// Host shape: migration-service is its OWN REGIONAL API on a per-service
// subdomain — `migration.api.[dev.|stage.]<apex>` — like tools-service
// (toolsApi.ts). On localhost the Vite proxy (`/migration-api` ->
// migration.api.dev.<apex>) stands in. Same auth source as every sibling: the
// admin id token under 'jwtToken', sent bare (no "Bearer " prefix).
//
// Types mirror services/agenticpos/migration-service/src/lib/repo.ts +
// transform.ts — that service keeps them local (not in shared-types yet).

export type MigrationStatus =
  | 'uploading' | 'submitted' | 'analysing' | 'mapped' | 'dry_run_ready' | 'approved'
  | 'backing_up' | 'applying' | 'stocking' | 'applied' | 'rolling_back' | 'rolled_back' | 'failed';

export type MigrationField =
  | 'groupKey' | 'name' | 'variantName'
  | 'option1Name' | 'option1Value' | 'option2Name' | 'option2Value' | 'option3Name' | 'option3Value'
  | 'sku' | 'barcode' | 'barcode2' | 'price' | 'cost' | 'category' | 'brand' | 'vendor'
  | 'description' | 'imageUrl' | 'stock' | 'reorderPoint' | 'active' | 'ignore';

export interface MigrationMapping {
  source?: string;
  columns: Record<string, MigrationField>;
  stockColumns?: Record<string, string>;
  priceColumns?: Record<string, string>;
  locationMap?: Record<string, string>;
  productTypeId?: string;
  profileId?: string;
}

export interface MigrationFile { key: string; name: string; size: number; contentType?: string; uploadedAt?: string; uploadedBy?: string }
export interface MigrationEvent { at: string; by?: string; kind: string; message: string; visibleToClient?: boolean }
export interface MigrationStats {
  rows?: number; items?: number; variants?: number; barcodes?: number; toCreate?: number; toSkip?: number;
  withIssues?: number; categoriesNew?: number; brandsNew?: number; stockRows?: number;
}
export interface MigrationProgress { total?: number; done?: number; failed?: number; stockDone?: number }

export interface MigrationJob {
  tenantId: string;
  jobId: string;
  tenantName?: string;
  status: MigrationStatus;
  clientStatus?: string;
  needsInput?: boolean;
  source?: string;
  sourceGuess?: string;
  notes?: string;
  files?: MigrationFile[];
  mainFileKey?: string;
  mapping?: MigrationMapping;
  preview?: { headers: string[]; rows: (string | number | null)[][] };
  unmappedLocations?: string[];
  stats?: MigrationStats;
  progress?: MigrationProgress;
  dryRunAt?: string;
  dryRunRequestedAt?: string;
  reportKey?: string;
  backupKey?: string;
  approvedBy?: string;
  approvedAt?: string;
  rubyPhotos?: boolean;
  error?: string;
  events?: MigrationEvent[];
  createdAt?: string;
  updatedAt?: string;
}

export interface MigrationVariant {
  name?: string;
  options?: Record<string, string>;
  sku?: string;
  barcodes: string[];
  price?: number;
  prices?: Record<string, number>;
  cost?: number;
  stock?: Record<string, number>;
  reorderPoint?: number;
  sourceRow?: number;
}

export type ApplyStatus = 'pending' | 'created' | 'draft_only' | 'published' | 'stocked' | 'failed' | 'rolled_back';

export interface MigrationItem {
  itemKey: string;
  name: string;
  description?: string;
  category?: string;
  brand?: string;
  vendor?: string;
  imageUrls?: string[];
  optionNames?: string[];
  variants: MigrationVariant[];
  issues: { code: string; message: string }[];
  match: { kind: 'new' | 'existing'; productId?: string; by?: string };
  decision: 'create' | 'skip';
  edited?: boolean;
  publishBlockers?: string[];
  apply?: { status: ApplyStatus; productId?: string; error?: string };
}

export interface MigrationProfile { profileId: string; name: string; source?: string; uses?: number; updatedAt?: string }

export type ItemPatch = Partial<Pick<MigrationItem, 'decision' | 'name' | 'category' | 'brand' | 'description' | 'variants'>>;

/** An API error that keeps the HTTP status (409 = not allowed in this state). */
export class MigrationApiError extends Error {
  status: number;
  constructor(message: string, status: number) { super(message); this.status = status; }
}

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  if (typeof window === 'undefined') return 'dev';
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch {
    // ignore
  }
  const h = window.location.hostname;
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — exported for tests. */
export function resolveMigrationHost(
  hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string,
): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/migration-api';
  const apex = (hostname || 'rubytill.com').replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://migration.api.${apex}`;
  if (stage === 'staging') return `https://migration.api.stage.${apex}`;
  return `https://migration.api.dev.${apex}`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_MIGRATION_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveMigrationHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  const text = await res.text();
  let body: any = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!res.ok) {
    const msg = (body && (body.error || body.message)) || `Request failed (${res.status})`;
    throw new MigrationApiError(String(msg), res.status);
  }
  return body as T;
}

const enc = encodeURIComponent;
const J = (tenantId: string, jobId: string) => `/migration/admin/tenants/${enc(tenantId)}/jobs/${enc(jobId)}`;
const post = (body?: unknown): RequestInit => ({ method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });

/** PUT a file straight to the presigned S3 URL, reporting 0..1 progress. */
export function putFile(url: string, file: File, contentType: string, onProgress?: (p: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable && onProgress) onProgress(e.loaded / e.total); };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed — network error'));
    xhr.send(file);
  });
}

export const migrationApi = {
  listJobs: () => call<{ jobs: MigrationJob[] }>('/migration/admin/jobs'),
  createJob: (input: { tenantId: string; source?: string; notes?: string }) =>
    call<{ job: MigrationJob }>('/migration/admin/jobs', post(input)),
  profiles: () => call<{ builtIn: MigrationProfile[]; saved: MigrationProfile[] }>('/migration/admin/profiles'),
  getJob: (t: string, j: string) =>
    call<{ job: MigrationJob; locations: { locationId: string; name: string }[]; productTypes: { productTypeId: string; name: string }[] }>(J(t, j)),
  presignUpload: (t: string, j: string, f: { filename: string; size: number; contentType?: string }) =>
    call<{ uploadUrl: string; contentType: string; key: string }>(`${J(t, j)}/uploads`, post(f)),
  saveMapping: (t: string, j: string, mapping: MigrationMapping, mainFileKey?: string) =>
    call<{ job: MigrationJob }>(`${J(t, j)}/mapping`, { method: 'PUT', body: JSON.stringify({ mapping, mainFileKey }) }),
  analyse: (t: string, j: string) => call<{ job: MigrationJob }>(`${J(t, j)}/analyse`, post({})),
  items: (t: string, j: string, cursor?: string, limit = 100) =>
    call<{ items: MigrationItem[]; next?: string }>(`${J(t, j)}/items?limit=${limit}${cursor ? `&cursor=${enc(cursor)}` : ''}`),
  patchItem: (t: string, j: string, itemKey: string, patch: ItemPatch) =>
    call<{ item: MigrationItem }>(`${J(t, j)}/items/${enc(itemKey)}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  dryRun: (t: string, j: string) => call<{ job?: MigrationJob }>(`${J(t, j)}/dry-run`, post({})),
  /** rubyPhotos: Ruby searches photos for products without one (charged to the client's Ruby Tokens). */
  approve: (t: string, j: string, rubyPhotos = false) => call<{ job: MigrationJob }>(`${J(t, j)}/approve`, post({ rubyPhotos })),
  apply: (t: string, j: string) => call<{ job?: MigrationJob }>(`${J(t, j)}/apply`, post({})),
  rollback: (t: string, j: string) => call<{ job?: MigrationJob }>(`${J(t, j)}/rollback`, post({ confirm: j })),
  message: (t: string, j: string, message: string, needsInput?: boolean) =>
    call<{ job: MigrationJob }>(`${J(t, j)}/message`, post({ message, needsInput: !!needsInput })),
  saveProfile: (t: string, j: string, name: string) =>
    call<{ profileId: string }>(`${J(t, j)}/save-profile`, post({ name })),
  downloadUrl: (t: string, j: string, key: string) =>
    call<{ url: string }>(`${J(t, j)}/download?key=${enc(key)}`),
};
